import { useMutation, useQueryClient } from 'react-query';

import { patchV2ApiOrganisationIdSettingsSettingsId } from 'src/client/spendesk-api';
import { useOrganisationId } from 'src/core/modules/app/hooks/useOrganisationId';
import { apiUrl } from 'src/core/utils/api';

import { type Setting } from '../setting';

type UpdateCostCenterData = Partial<{
  // because it's a PATCH
  name: string;
  ownerId: string;
  viewerIds: string[];
  companyIds: string[];
  autoAssignFutureEntities: boolean;
}>;

type UpdatePayload = {
  data?: {
    name?: string;
    ownerId?: string;
    viewerIds?: string[];
  };
  autoAssignFutureEntities?: boolean;
  companyIds?: string[];
};

const updateEntitySpecificCostCenter = async (
  setting: Setting,
  payloadData?: UpdatePayload['data'],
): Promise<void> => {
  if (!setting.entities || setting.entities.length === 0) {
    throw new Error('No entities found for entity-specific setting');
  }
  // since single entity cost center has only one entity, we can use the first one, and use the PUT of single entity cost center
  const response = await fetch(
    apiUrl(
      `/cost-centers/${setting.entities[0].costCenterId}`,
      setting.entities[0].companyId,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: payloadData?.name,
        ownerId: payloadData?.ownerId,
        viewerIds: payloadData?.viewerIds,
      }),
    },
  );
  if (!response?.ok) {
    throw new Error('Failed to update cost center');
  }
};

const updateCrossEntityCostCenter = async (
  organisationId: string,
  setting: Setting,
  payload: UpdatePayload,
): Promise<void> => {
  const response = await patchV2ApiOrganisationIdSettingsSettingsId({
    path: {
      organisationId,
      settingsId: setting.id,
    },
    body: {
      autoAssignFutureEntities: payload.autoAssignFutureEntities,
      companyIds: payload.companyIds,
      data: payload.data,
    },
  });
  if (response.statusCode !== 200) {
    throw new Error('Failed to update cost center');
  }
};

const updateOrganisationSetting = async (
  organisationId: string,
  setting: Setting,
  data: UpdateCostCenterData,
): Promise<void> => {
  const payload: UpdatePayload = {};
  if (Object.keys(data).length === 0) {
    return;
  }

  // for cost center details
  if ('name' in data || 'ownerId' in data || 'viewerIds' in data) {
    payload.data = {};
    if (data.name !== undefined) {
      payload.data.name = data.name;
    }
    if (data.ownerId !== undefined) {
      payload.data.ownerId = data.ownerId;
    }
    if (data.viewerIds !== undefined) {
      payload.data.viewerIds = data.viewerIds;
    }
  }

  if (data.autoAssignFutureEntities !== undefined) {
    payload.autoAssignFutureEntities = data.autoAssignFutureEntities;
  }
  // for cost center assignment
  if ('companyIds' in data) {
    payload.companyIds = data.companyIds;
  }

  if (setting.type === 'cross-entity') {
    await updateCrossEntityCostCenter(organisationId, setting, payload);
  } else if (
    setting.type === 'entity-specific' &&
    setting.entities?.length === 1
  ) {
    await updateEntitySpecificCostCenter(setting, payload.data);
  }
};

export const useUpdateOrganisationSetting = (setting: Setting) => {
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: UpdateCostCenterData) =>
      updateOrganisationSetting(organisationId, setting, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settings', organisationId]);
      },
    },
  );
};
